import { FormEvent, useRef, useState } from 'react';
import { ITipsFeedbackItem, TFeedbackStatusUpdate } from '../../../../interfaces/tips-feedback/ITipsFeedback';
import { useResolveTipsFeedbackMutation, useUpdateTipsFeedbackStatusMutation } from '../../../../store/chatbox/chatboxApi';
import { canManageFeedback, canResolveFeedback, feedbackErrorMessage, feedbackStatusLabel } from '../utils';

const inputStyle = 'mt-1 min-w-0 w-full rounded-lg border border-gray-300 dark:border-borderColor-darkMode bg-white dark:bg-black p-2 text-sm text-gray-900 dark:text-white disabled:opacity-50';
const buttonStyle = 'w-full rounded-xl bg-[#00330f] px-4 py-2 text-sm font-semibold text-white dark:bg-white dark:text-black disabled:opacity-50 sm:w-auto';

const TipsFeedbackWorkflow = ({ item, disabled, onSaved }: {
    item: ITipsFeedbackItem;
    disabled?: boolean;
    onSaved: () => void;
}) => {
    const [status, setStatus] = useState<TFeedbackStatusUpdate | ''>('');
    const [reason, setReason] = useState('');
    const [investigation, setInvestigation] = useState(item.investigation || '');
    const [resolution, setResolution] = useState(item.resolution || '');
    const [ragParameter, setRagParameter] = useState(item.ragParameter || '');
    const [notifyClient, setNotifyClient] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [warning, setWarning] = useState('');
    const savingRef = useRef(false);
    const [updateStatus, { isLoading: updating }] = useUpdateTipsFeedbackStatusMutation();
    const [resolveFeedback, { isLoading: resolving }] = useResolveTipsFeedbackMutation();
    const busy = updating || resolving || disabled;
    const canManage = canManageFeedback(item.workflowStatus);
    const canResolve = canResolveFeedback(item);

    const saveStatus = async (event: FormEvent) => {
        event.preventDefault();
        if (busy || savingRef.current || !canManage) return;
        setError('');
        setSuccess('');
        setWarning('');
        if (!status || (status === 'cannot_resolve' && !reason.trim())) {
            setError('Select a status and provide a reason when feedback cannot be resolved.');
            return;
        }
        savingRef.current = true;
        try {
            const result = await updateStatus({
                feedbackId: item._id,
                workflowStatus: status,
                ...(reason.trim() ? status === 'cannot_resolve' ? { reason: reason.trim() } : { note: reason.trim() } : {}),
            }).unwrap();
            if (result.success !== true) {
                setError(result.message || 'The feedback status was not updated.');
                return;
            }
            setStatus('');
            setReason('');
            setSuccess('Feedback status updated.');
            onSaved();
        } catch (requestError) {
            setError(feedbackErrorMessage(requestError, 'Unable to update feedback status. Please try again.'));
        } finally {
            savingRef.current = false;
        }
    };

    const saveResolution = async (event: FormEvent) => {
        event.preventDefault();
        if (busy || savingRef.current || !canResolve) return;
        setError('');
        setSuccess('');
        setWarning('');
        if (!resolution.trim()) {
            setError('Enter a resolution before resolving feedback.');
            return;
        }
        savingRef.current = true;
        try {
            const result = await resolveFeedback({
                feedbackId: item._id,
                ...(investigation.trim() ? { investigation: investigation.trim() } : {}),
                resolution: resolution.trim(),
                ...(ragParameter.trim() ? { ragParameter: ragParameter.trim() } : {}),
                notifyClient,
            }).unwrap();
            if (result.success !== true) {
                setWarning(result.message || 'Resolution was saved, but tip regeneration did not complete.');
                onSaved();
                return;
            }
            if (notifyClient && result.clientNotificationStatus === 'failed') {
                setWarning('The replacement tip was generated, but the client notification failed.');
            } else if (notifyClient && result.clientNotificationStatus === 'pending_configuration') {
                setWarning('The replacement tip was generated, but the client was not notified. Check the notification configuration and reporter email.');
            }
            setNotifyClient(false);
            setSuccess('Feedback resolved.');
            onSaved();
        } catch (requestError) {
            setError(feedbackErrorMessage(requestError, 'Unable to resolve feedback. Please try again.'));
            onSaved();
        } finally {
            savingRef.current = false;
        }
    };

    return (
        <section className='min-w-0'>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>Manage feedback</h3>
            <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>Current status: {feedbackStatusLabel(item.workflowStatus, item.status)}</p>
            {item.reason && <p className='mt-1 text-sm whitespace-pre-wrap dark:text-gray-300'>Reason: {item.reason}</p>}
            {error && <p role='alert' className='mt-3 text-sm text-red-600 dark:text-red-400'>{error}</p>}
            {success && <p role='status' className='mt-3 text-sm text-green-700 dark:text-green-400'>{success}</p>}
            {warning && <p role='status' className='mt-3 text-sm text-amber-700 dark:text-amber-400'>{warning}</p>}
            {!canManage && (
                <div className='mt-3 space-y-3 text-sm dark:text-gray-300'>
                    <p className='text-gray-500 dark:text-gray-400'>Status changes are unavailable for this feedback.</p>
                    {[
                        { label: 'Investigation', value: item.investigation },
                        { label: 'Resolution', value: item.resolution },
                        { label: 'RAG parameter', value: item.ragParameter },
                    ].filter((field) => field.value).map((field) => (
                        <div key={field.label}>
                            <h5 className='font-semibold'>{field.label}</h5>
                            <p className='mt-1 whitespace-pre-wrap break-words'>{field.value}</p>
                        </div>
                    ))}
                </div>
            )}
            {canManage && !item.tipId && <p className='mt-3 text-sm text-gray-500 dark:text-gray-400'>This feedback has no linked tip to regenerate.</p>}
            {(canManage || canResolve) && <div className='mt-4 grid items-start gap-4 lg:grid-cols-2'>
                {canManage && <form onSubmit={saveStatus} className='min-w-0 rounded-xl border border-gray-200 dark:border-borderColor-darkMode p-4'>
                    <fieldset disabled={busy} className='min-w-0 space-y-3'>
                        <legend className='mb-3 text-sm font-semibold dark:text-white'>Update status</legend>
                        <label className='block text-sm dark:text-gray-300'>
                            Status
                            <select required value={status} onChange={(event) => setStatus(event.target.value as TFeedbackStatusUpdate | '')} className={inputStyle}>
                                <option value=''>Select status</option>
                                <option value='submitted'>Submitted</option>
                                <option value='investigating'>Leo is Investigating</option>
                                <option value='cannot_resolve'>Cannot resolve</option>
                            </select>
                        </label>
                        <label className='block text-sm dark:text-gray-300'>
                            {status === 'cannot_resolve' ? 'Reason (required)' : 'Note (optional)'}
                            <textarea rows={4} required={status === 'cannot_resolve'} value={reason} onChange={(event) => setReason(event.target.value)} className={inputStyle} />
                        </label>
                        <button type='submit' disabled={busy || !status || (status === 'cannot_resolve' && !reason.trim())} className={buttonStyle}>
                            {updating ? 'Saving...' : 'Save status'}
                        </button>
                    </fieldset>
                </form>}
                {canResolve && <form onSubmit={saveResolution} className='min-w-0 rounded-xl border border-gray-200 dark:border-borderColor-darkMode p-4'>
                    <fieldset disabled={busy} className='min-w-0 space-y-3'>
                        <legend className='mb-3 text-sm font-semibold dark:text-white'>{canManage ? 'Resolve feedback' : 'Retry tip generation'}</legend>
                        <label className='block text-sm dark:text-gray-300'>
                            Investigation (optional)
                            <textarea rows={3} value={investigation} onChange={(event) => setInvestigation(event.target.value)} className={inputStyle} />
                        </label>
                        <label className='block text-sm dark:text-gray-300'>
                            Resolution
                            <textarea required rows={3} value={resolution} onChange={(event) => setResolution(event.target.value)} className={inputStyle} />
                        </label>
                        <label className='block text-sm dark:text-gray-300'>
                            RAG parameter (optional)
                            <span className='block mt-1 text-xs text-gray-500 dark:text-gray-400'>Guidance for future recommendations. Uses the resolution when left blank.</span>
                            <textarea rows={3} value={ragParameter} onChange={(event) => setRagParameter(event.target.value)} className={inputStyle} />
                        </label>
                        <label className='flex items-center gap-2 text-sm dark:text-gray-300'>
                            <input type='checkbox' checked={notifyClient} onChange={(event) => setNotifyClient(event.target.checked)} />
                            Notify client
                        </label>
                        <button type='submit' disabled={busy || !resolution.trim()} className={buttonStyle}>
                            {resolving ? 'Generating replacement tip...' : canManage ? 'Resolve feedback' : 'Retry tip generation'}
                        </button>
                    </fieldset>
                </form>}
            </div>}
        </section>
    );
};

export default TipsFeedbackWorkflow;
